import axios from 'axios'
import * as Sentry from '@sentry/vue'
import { z } from 'zod'

import { AllauthError, makeAllauthRequestAndParseErrors } from '@/utils/auth'
import { getMailbox, getUnreadCount } from '@/services/api/providerMessaging'
import { BrowserStore } from '@/utils/BrowserStore'
import { FEATURES } from '@/constants/features'

const UNREAD_POLL_INTERVAL = 300000

const unreadStore = new BrowserStore(
  'unreadMessageCache',
  z.object({ count: z.number().default(0), ts: z.number().default(0) }),
)

function getCachedUnread() {
  const { count, ts } = unreadStore.load()
  if (Date.now() - ts < UNREAD_POLL_INTERVAL) return count
  return null
}

function setCachedUnread(count) {
  unreadStore.save({ count, ts: Date.now() })
}

const state = {
  isAuthenticated: false, // Track authentication status
  current_user : null,
  loading: true,
  unreadMessageCount: 0,
  hasActiveProviderMailbox: false,
}

const mutations = {
  SET_USER(state, user) {
    state.isAuthenticated = true
    state.current_user = user
    Sentry.setUser({
      email: user.email,
      id: user.id,
    })
  },
  SET_USER_ENTITIES(state, entities) {
    if (state.current_user) {
      state.current_user.user_entities = entities
    }
  },
  CLEAR_USER(state) {
    state.isAuthenticated = false
    state.current_user = null
    state.hasActiveProviderMailbox = false
    Sentry.setUser(null)
  },
  SET_LOADING(state, value){
    state.loading = value
  },
  SET_UNREAD_MESSAGE_COUNT(state, count) {
    state.unreadMessageCount = count
  },
  SET_HAS_ACTIVE_PROVIDER_MAILBOX(state, hasActiveMailbox) {
    state.hasActiveProviderMailbox = hasActiveMailbox
  },
}

const actions = {
  async login({ dispatch }, credentials) {
    let response
    try {
      response = await makeAllauthRequestAndParseErrors({
        method: 'post',
        url: '_allauth/browser/v1/auth/login',
        data: credentials })
    } catch (error) {
      // Browser auth cookies are shared across tabs, but each tab has its own
      // in-memory Vuex state. If another tab logged in first, allauth returns
      // 409 (already authenticated) here. Rehydrate and verify that shared
      // session rather than stranding this stale tab on the login form.
      if (error.originalError?.response?.status === 409) {
        const existingUser = await dispatch('fetchUser')
        if (existingUser) return
      }
      throw error
    }
    if (response.status === 401) {
      // email/password correct, but additional action is needed, like verifying email
      throw new AllauthError({ non_field_errors: ['Login failed']})
    }
  },
  async fetchUser({ commit, dispatch }) {
    commit('SET_LOADING',true)
    try {
      const { data } = await axios.get('_allauth/browser/v1/auth/session', { excludeTenantSlug: true })
      const userdata = data.data.user
      await dispatch('tenant/updateTenantsFromUser', userdata, { root: true })
      commit('SET_USER', userdata)
      await dispatch('fetchProviderMailboxStatus')

      // CSRF token no longer snapshotted here: the global axios request
      // interceptor (main.js) now reads it fresh from the cookie per request,
      // which also covers the post-login token rotation this used to handle.

      return userdata
    } catch (error) {
      commit('CLEAR_USER')
      if (error.response && error.response.status !== 200) {
        return null // no user logged in
      } else {
        throw error //some other error
      }
    } finally {
      commit('SET_LOADING',false)
    }
  },
  async fetchProviderMailboxStatus({ commit, state, rootState }) {
    const tenantSlug = rootState.tenant.current_tenant_slug
    const tenantFeatures = state.current_user?.tenant_access?.[tenantSlug]?.features || []
    if (!tenantFeatures.includes(FEATURES.MODULE_PROVIDER_MESSAGING)) {
      commit('SET_HAS_ACTIVE_PROVIDER_MAILBOX', false)
      return
    }

    try {
      const mailbox = await getMailbox()
      commit('SET_HAS_ACTIVE_PROVIDER_MAILBOX', mailbox.status === 'active')
    } catch {
      // A 404 means no mailbox; an inactive mailbox is also intentionally hidden.
      commit('SET_HAS_ACTIVE_PROVIDER_MAILBOX', false)
    }
  },
  async fetchUnreadMessageCount({ commit, getters }, { skipCache } = {}) {
    if (!getters.hasActiveProviderMailbox) return
    if (!skipCache) {
      const cached = getCachedUnread()
      if (cached !== null) {
        commit('SET_UNREAD_MESSAGE_COUNT', cached)
        return
      }
    }
    try {
      const data = await getUnreadCount()
      commit('SET_UNREAD_MESSAGE_COUNT', data.unread_count)
      setCachedUnread(data.unread_count)
    } catch {
      // Silently ignore — polling will retry
    }
  },
  async logout({commit, dispatch}) {
    try {
      await makeAllauthRequestAndParseErrors({
        method: 'delete',
        url: '_allauth/browser/v1/auth/session',
      })
      commit('CLEAR_USER')
      unreadStore.clear()
      await dispatch('tenant/clearTenant_slug', null, { root: true })
      await dispatch('featureConfig/clearConfig', null, { root: true })
      await dispatch('rolePreview/disablePreview', null, { root: true })
    }
    catch (error) {
      console.error('Logout failed:', error)
    }
  }
}
const getters = {
  isAuthenticated: state => state.isAuthenticated,
  current_user: state => state.current_user,
  loading : state => state.loading,
  // Deaconess/IHCI SRA #4 (90-day password rotation). Backend is the
  // enforcement boundary (HasTenantRole 403s with "password_expired"); this
  // just lets the router proactively redirect before the user hits that 403.
  passwordExpired: state => Boolean(state.current_user?.password_expired),
  unreadMessageCount: state => state.unreadMessageCount,
  hasActiveProviderMailbox: state => state.hasActiveProviderMailbox,
}



export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
